import React, { useEffect, useState } from 'react';
import SectionTitle from '../Common/SectionTitle';

const TallyPrimeAWS = () => {
  // State for the AWS package selections
  const [personalPack, setPersonalPack] = useState("1");
  const [serverPack, setServerPack] = useState("8");

  // Set correct page title and scroll to top when component mounts
  useEffect(() => {
    document.title = "TallyPrime on AWS | Secure Cloud Accounting Software for Businesses";
    window.scrollTo(0, 0);
    
    // Add Font Awesome script for icons
    const fontAwesomeScript = document.createElement('script');
    fontAwesomeScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/js/all.min.js'; 
    fontAwesomeScript.crossOrigin = 'anonymous';
    document.head.appendChild(fontAwesomeScript);
    
    // Apply consistent font styling
    const fontStyle = document.createElement('style');
    fontStyle.textContent = `
      * {
        font-family: "Roboto Regular", "Roboto", sans-serif !important;
      }
      
      /* Keep special styling for price numbers */
      .price-amount {
        font-family: "Roboto Regular", "Roboto", sans-serif !important;
        font-weight: 700 !important;
      }

      /* TallyPrime AWS specific styles */
      .feature-list {
        list-style: none;
        padding-left: 0;
      }
      
      .feature-list li {
        padding: 8px 0;
        position: relative;
        padding-left: 28px;
      }
      
      .feature-list li:before {
        content: none;
      }
      
      .feature-list li img {
        position: absolute;
        left: 0;
        top: 10px;
        width: 16px;
        height: 16px;
      }
      
      .feature-list li.unlimited-usage {
        color: #0d6efd;
        font-weight: 500;
      }

      .bg-light-cream {
        background-color: #f9f9f9;
      }
      
      .text-theme {
        color: #0d6efd;
      }
      
      .text-gray {
        color: #6c757d;
      }
      
      .brush-underline-yellow {
        position: relative;
        display: inline-block;
      }
      
      .brush-underline-yellow:after {
        content: "";
        display: block;
        position: absolute;
        bottom: -5px;
        left: 0;
        width: 100%;
        height: 12px;
        background-image: url('https://resources.tallysolutions.com/wp-content/themes/tally/assets/images/brush-yellow.svg');
        background-repeat: no-repeat;
        background-size: 100% 100%;
        z-index: -1;
      }

      .custom-shadow-bottom {
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
        transition: all 0.3s ease;
      }

      .custom-shadow-bottom:hover {
        transform: translateY(-5px);
      }

      .card {
        border-radius: 12px;
        overflow: hidden;
      }

      .arrow-animation .arrow {
        transition: transform 0.3s ease;
      }

      .arrow-animation:hover .arrow {
        transform: translate(3px, -3px);
      }
      
      .pricing-card-badge {
        position: absolute;
        top: 0;
        right: 0;
        background-color: #0d6efd;
        color: white;
        padding: 5px 15px;
        font-size: 0.75rem;
        border-bottom-left-radius: 8px;
      }
      
      .key-capabilities-item {
        background-color: white;
        border-radius: 12px;
        padding: 20px;
        margin-bottom: 20px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.08);
        transition: all 0.3s ease;
      }
      
      .key-capabilities-item:hover {
        transform: translateY(-5px);
        box-shadow: 0 8px 24px rgba(0,0,0,0.1);
      }
      
      .aws-desc {
        min-height: 50px;
      }
      
      .pricing-section {
        position: relative;
        overflow: hidden;
      }
      
      .pricing-section:before {
        content: '';
        position: absolute;
        top: -50px;
        right: -50px;
        width: 100px;
        height: 100px;
        border-radius: 50%;
        background-color: rgba(13, 110, 253, 0.1);
        z-index: 0;
      }
      
      .pricing-section:after {
        content: '';
        position: absolute;
        bottom: -100px;
        left: -100px;
        width: 200px;
        height: 200px;
        border-radius: 50%;
        background-color: rgba(13, 110, 253, 0.05);
        z-index: 0;
      }
      
      .card {
        position: relative;
        z-index: 1;
      }
      
      .card:hover {
        border-color: #0d6efd !important;
      }
      
      .btn-primary {
        border-radius: 50px;
        padding: 10px 25px;
        font-weight: 600;
        transition: all 0.3s ease;
      }
      
      .btn-primary:hover {
        transform: translateY(-3px);
        box-shadow: 0 5px 15px rgba(13, 110, 253, 0.3);
      }
      
      select.awsPackageSelect {
        border-radius: 8px;
        padding: 10px;
        border-color: #dee2e6;
        transition: all 0.3s ease;
      }
      
      select.awsPackageSelect:focus {
        border-color: #0d6efd;
        box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
        outline: none;
      }
    `;
    document.head.appendChild(fontStyle);

    return () => {
      document.title = "Digisoft Services";
      document.head.removeChild(fontStyle);
      document.head.removeChild(fontAwesomeScript);
    };
  }, []);

  // Handle personal pack selection change
  const handlePersonalPackChange = (e) => {
    setPersonalPack(e.target.value);
  };

  // Handle server pack selection change
  const handleServerPackChange = (e) => {
    setServerPack(e.target.value);
  };

  // Hero section background style
  const heroBackgroundStyle = {
    backgroundImage: `url(${process.env.PUBLIC_URL}/assets/img/hero-bg-2.jpg)`,
    backgroundPosition: 'center center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    opacity: 0.1,
    margin: 0,
    padding: 0
  };

  // Key capabilities data
  const keyCapabilities = [
    {
      title: "Secure Access Anywhere",
      description: "Securely access TallyPrime from anywhere, anytime"
    },
    {
      title: "Dual-Layer Security",
      description: "Secure log-in with dual-layer security"
    },
    {
      title: "Self Service Portal",
      description: "Self Service Portal (SSP) for management"
    },
    {
      title: "Automated Backups",
      description: "Automated and encrypted backups"
    },
    {
      title: "Selective Data Sharing",
      description: "Share data selectively with team members"
    },
    {
      title: "Effortless Upgrades",
      description: "Effortless upgrades to new releases"
    },
    {
      title: "App Integration",
      description: "Integration with other applications"
    },
    {
      title: "Affordable Plans",
      description: "Affordable and scalable plans (starting at ₹600/user/month)"
    }
  ];

  // FAQ data
  const faqs = [
    {
      question: "What is data usage limit and scheduled usage time?",
      answer: "There are no limits on data usage. TallyPrime powered by AWS provides 24/7 access with unlimited data usage, allowing you to work efficiently without any restrictions."
    },
    {
      question: "What are the benefits of TallyPrime powered by AWS?",
      answer: "TallyPrime powered by AWS offers remote access to your TallyPrime data, enhanced collaboration capabilities, affordability with no upfront infrastructure costs, and minimal IT infrastructure requirements."
    },
    {
      question: "How do I access this solution (AWS)?",
      answer: "You can access TallyPrime powered by AWS by logging in with the credentials provided to you through a web browser or virtual client from any device with internet connectivity."
    },
    {
      question: "What is the cost?",
      answer: "TallyPrime powered by AWS starts at ₹600 per user per month, with various plans available based on your business needs and team size."
    },
    {
      question: "How do I purchase?",
      answer: "You can purchase TallyPrime powered by AWS by contacting your nearest authorized Tally partner who will guide you through the process and help set up the solution for your business."
    },
    {
      question: "Can I buy only TallyPrime powered by AWS without a license?",
      answer: "No, a valid TallyPrime license is required to use TallyPrime powered by AWS, as it is a hosting solution for the TallyPrime software."
    }
  ];

  // Testimonials data
  const testimonials = [
    {
      company: "Bajaj Electricals",
      quote: "TallyPrime on AWS simplified our distribution network, making billing, data integration, and supply chain management effortless across multiple locations."
    },
    {
      company: "Mohan & Chandrashekhar CA Firm",
      quote: "Cloud access has transformed our accounting and client services by enabling our team to work remotely while maintaining data security."
    },
    {
      company: "Geetha Enterprises",
      quote: "The flexibility to access our business data from anywhere has been a game-changer for our operations and decision-making process."
    }
  ];

  // Personal pack details mapping
  const personalPackDetails = {
    "1": {
      price: "600",
      gst: "108",
      specs: ["2 vCPU", "4 GB RAM", "5 GB Storage", "5 GB Backup Storage", "24*7, Unlimited Usage"]
    },
    "2": {
      price: "600",
      gst: "108",
      specs: ["2 vCPU", "4 GB RAM", "5 GB Storage", "5 GB Backup Storage", "24*7, Unlimited Usage"]
    },
    "4": {
      price: "450",
      gst: "81",
      specs: ["2 vCPU", "8 GB RAM", "10 GB Storage", "10 GB Backup Storage", "24*7, Unlimited Usage"]
    },
    "8": {
      price: "450",
      gst: "81",
      specs: ["4 vCPU", "16 GB RAM", "20 GB Storage", "20 GB Backup Storage", "24*7, Unlimited Usage"]
    },
    "12": {
      price: "450",
      gst: "81",
      specs: ["8 vCPU", "32 GB RAM", "40 GB Storage", "40 GB Backup Storage", "24*7, Unlimited Usage"]
    },
    "16": {
      price: "450",
      gst: "81",
      specs: ["8 vCPU", "32 GB RAM", "40 GB Storage", "40 GB Backup Storage", "24*7, Unlimited Usage"]
    }
  };

  // Server pack details mapping
  const serverPackDetails = {
    "8": {
      price: "750",
      gst: "135",
      specs: ["4 vCPU", "32 GB RAM", "30 GB Storage", "20 GB Backup Storage", "24*7, Unlimited Usage"]
    },
    "12": {
      price: "750",
      gst: "135",
      specs: ["8 vCPU", "64 GB RAM", "30 GB Storage", "40 GB Backup Storage", "24*7, Unlimited Usage"]
    },
    "16": {
      price: "750",
      gst: "135",
      specs: ["8 vCPU", "64 GB RAM", "40 GB Storage", "40 GB Backup Storage", "24*7, Unlimited Usage"]
    },
    "24": {
      price: "750",
      gst: "135",
      specs: ["16 vCPU", "128 GB RAM", "50 GB Storage", "50 GB Backup Storage", "24*7, Unlimited Usage"]
    },
    "32": {
      price: "750",
      gst: "135",
      specs: ["16 vCPU", "128 GB RAM", "50 GB Storage", "80 GB Backup Storage", "24*7, Unlimited Usage"]
    },
    "48": {
      price: "750",
      gst: "135",
      specs: ["32 vCPU", "256 GB RAM", "60 GB Storage", "100 GB Backup Storage", "24*7, Unlimited Usage"]
    },
    "64": {
      price: "750",
      gst: "135",
      specs: ["32 vCPU", "256 GB RAM", "75 GB Storage", "100 GB Backup Storage", "24*7, Unlimited Usage"]
    },
    "96": {
      price: "750",
      gst: "135",
      specs: ["48 vCPU", "348 GB RAM", "95 GB Storage", "100 GB Backup Storage", "24*7, Unlimited Usage"]
    },
    "128": {
      price: "750",
      gst: "135",
      specs: ["64 vCPU", "512 GB RAM", "120 GB Storage", "100 GB Backup Storage", "24*7, Unlimited Usage"]
    }
  };

  // Personal pack name mapping
  const personalPackNames = {
    "1": "Personal Pack",
    "2": "Regular Pack",
    "4": "Regular Plus Pack",
    "8": "Regular Pro Pack",
    "12": "Performance Pack",
    "16": "Performance Plus Pack"
  };

  // Server pack name mapping
  const serverPackNames = {
    "8": "Performance Pack",
    "12": "Performance Max Pack",
    "16": "Performance Plus Pack",
    "24": "Performance Max Plus Pack",
    "32": "Performance Pro Pack",
    "48": "Enterprise Pack",
    "64": "Enterprise Plus Pack",
    "96": "Enterprise Max Plus Pack",
    "128": "Enterprise Pro Pack"
  };

  return (
    <div id="tallyprime-aws-page" className="tallyprime-aws-page p-0">
      {/* Hero Section */}
      <section className="tallyprime-aws-hero pt-5 section dark-background position-relative">
        <div style={heroBackgroundStyle}></div>
        <div className="container py-5">
          <div className="row py-5 align-items-center">
            <div className="col-lg-6">
              <div className="position-relative" style={{ zIndex: 1 }}>
                <div className="d-inline-block px-3 py-1 rounded-pill mb-3" style={{ backgroundColor: "black", color: "gold", fontWeight: "500", fontSize: "14px" }}>
                  #PoweredByAWS
                </div>
                <h1 className="display-5 fw-bold mb-4">TallyPrime Powered by AWS, Starting at 600 INR</h1>
                <p className="lead mb-4 pb-3" style={{ color: "white" }}>
                  <span className="d-inline-block mb-2">
                    <img 
                      src="https://uxwing.com/wp-content/themes/uxwing/download/checkmark-cross/checkmark-white-round-icon.png" 
                      alt="Check" 
                      className="me-2"
                      style={{ width: "16px", height: "16px" }}
                    />
                    24*7 Secured Cloud Access
                  </span>
                  <span className="d-inline-block mb-2 ms-lg-3">
                    <img 
                      src="https://uxwing.com/wp-content/themes/uxwing/download/checkmark-cross/checkmark-white-round-icon.png" 
                      alt="Check" 
                      className="me-2"
                      style={{ width: "16px", height: "16px" }}
                    />
                    Flexible Plans
                  </span>
                  <br />
                  <span className="d-inline-block mb-2">
                    <img 
                      src="https://uxwing.com/wp-content/themes/uxwing/download/checkmark-cross/checkmark-white-round-icon.png" 
                      alt="Check" 
                      className="me-2"
                      style={{ width: "16px", height: "16px" }}
                    />
                    Platform Agnostic Solution
                  </span>
                  <span className="d-inline-block mb-2 ms-lg-3">
                    <img 
                      src="https://uxwing.com/wp-content/themes/uxwing/download/checkmark-cross/checkmark-white-round-icon.png" 
                      alt="Check" 
                      className="me-2"
                      style={{ width: "16px", height: "16px" }}
                    />
                    Unlimited Usage
                  </span>
                </p>
                <div className="d-flex flex-wrap gap-3">
                  <a href="#tponaws" className="btn btn-primary btn-lg px-4 py-2">
                    Explore Plans
                    <i className="bi bi-arrow-right ms-2"></i>
                  </a>
              {/*    <a href="#contact" className="btn btn-outline-primary btn-lg px-4 py-2" style={{ backgroundColor: "transparent", color: "white" }}>Contact Us</a> */}
                </div>
              </div>
            </div>
            <div className="col-lg-6 text-center">
              <div className="position-relative">
                <div className="position-absolute" style={{ background: "rgba(13, 110, 253, 0.1)", width: "200px", height: "200px", borderRadius: "50%", top: "-30px", right: "-30px", zIndex: 0 }}></div>
                <img 
                  src="https://resources.tallysolutions.com/wp-content/themes/tally/assets/images/tally-software-service/tp-powered-by-aws-banner.webp" 
                  alt="TallyPrime powered by AWS" 
                  className="img-fluid position-relative"
                  style={{ maxHeight: '400px', zIndex: 1, borderRadius: "12px" }}
                />
                <div className="position-absolute" style={{ background: "rgba(13, 110, 253, 0.05)", width: "150px", height: "150px", borderRadius: "50%", bottom: "-20px", left: "-20px", zIndex: 0 }}></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What is TallyPrime powered by AWS Section */}
      <section className="what-is-tallyprime-aws py-5 section light-background">
        <div className="container py-5">
          <SectionTitle 
            title="What is TallyPrime powered by AWS?" 
            description1="Cloud" 
            description2="Solution" 
          />
          
          <div className="row mt-5">
            <div className="col-lg-8 mx-auto text-center">
              <p className="lead">
                TallyPrime powered by AWS helps you securely access TallyPrime and your business data anytime, anywhere. 
                It's enabled by a virtual machine dedicated to your business, offering greater flexibility for collaboration.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Key Capabilities */}
      <section className="key-capabilities py-5 section bg-light">
        <div className="container py-5">
          <div className="row mb-5">
            <div className="col-lg-6">
              <SectionTitle 
                title="Key Capabilities" 
                description1="Enhanced" 
                description2="Features" 
              />
              
              <div className="row mt-4">
                {keyCapabilities.map((capability, index) => (
                  <div className="col-md-6 mb-4" key={index}>
                    <div className="key-capabilities-item">
                      <div className="d-flex align-items-center mb-2">
                        <img 
                          src="https://resources.tallysolutions.com/wp-content/themes/tally/assets/images/customer/blue-tick.svg" 
                          alt="Check" 
                          className="me-2"
                          style={{ width: "18px", height: "18px" }}
                        />
                        <h4 className="h5 mb-0 text-theme">{capability.title}</h4>
                      </div>
                      <p className="mb-0 mt-2 ps-4">{capability.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-4 text-center text-lg-start">
                <a href="#tponaws" className="btn btn-primary px-4 py-2">
                  Explore Pricing
                  <i className="bi bi-arrow-right ms-2"></i>
                </a>
              </div>
            </div>
            <div className="col-lg-6 d-flex align-items-center justify-content-center">
              <div className="position-relative">
                <div className="position-absolute" style={{ background: "rgba(13, 110, 253, 0.1)", width: "200px", height: "200px", borderRadius: "50%", top: "-20px", right: "-30px", zIndex: 0 }}></div>
                <img 
                  src="https://resources.tallysolutions.com/wp-content/themes/tally/assets/images/tally-software-service/tp-poweredby-aws-key-capabilities.webp" 
                  alt="Key Capabilities" 
                  className="img-fluid position-relative"
                  style={{ zIndex: 1, borderRadius: "12px", boxShadow: "0 10px 30px rgba(0,0,0,0.1)" }}
                />
                <div className="position-absolute" style={{ background: "rgba(13, 110, 253, 0.05)", width: "150px", height: "150px", borderRadius: "50%", bottom: "-30px", left: "-20px", zIndex: 0 }}></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="testimonials py-5 section">
        <div className="container py-5">
          <div className="text-center mb-5">
            <SectionTitle 
              title="Built for Businesses of All Sizes" 
              description1="Customer" 
              description2="Success" 
            />
          </div>
          
          <div className="row mt-5 position-relative">
            <div className="position-absolute" style={{ background: "rgba(13, 110, 253, 0.05)", width: "300px", height: "300px", borderRadius: "50%", top: "-50px", right: "-100px", zIndex: 0 }}></div>
            
            {testimonials.map((item, index) => (
              <div className="col-md-4 mb-4" key={index}>
                <div className="card h-100 p-4 border-0 custom-shadow-bottom position-relative" style={{ zIndex: 1 }}>
                  <div className="mb-4">
                    <img 
                      src="https://resources.tallysolutions.com/wp-content/themes/tally/assets/images/testimonial-quote.svg" 
                      alt="Quote" 
                      className="mb-3" 
                      style={{ width: "40px", opacity: 0.7 }}
                    />
                  </div>
                  <p className="card-text mb-4">{item.quote}</p>
                  <div className="mt-auto">
                    <div className="d-flex align-items-center">
                      <div className="flex-shrink-0 me-3">
                        <div className="testimonial-avatar rounded-circle d-flex align-items-center justify-content-center" style={{ width: "50px", height: "50px", backgroundColor: "#f1f5ff", color: "#0d6efd", fontWeight: "bold", fontSize: "18px" }}>
                          {item.company.charAt(0)}
                        </div>
                      </div>
                      <div>
                        <h5 className="mb-1">{item.company}</h5>
                        <p className="mb-0 text-muted" style={{ fontSize: "14px" }}>Verified Customer</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            
            <div className="position-absolute" style={{ background: "rgba(13, 110, 253, 0.05)", width: "200px", height: "200px", borderRadius: "50%", bottom: "-50px", left: "-100px", zIndex: 0 }}></div>
          </div>
        </div>
      </section>

      {/* Pricing Plans */}
      <section className="bg-light-cream py-lg-5 py-4 pricing-section">
        <div id="tponaws" className="container py-5">
          <div className="row">
            <div className="col-lg-12">
              <div className="text-center mb-lg-4">
                <h2><span className="brush-underline-yellow">TallyPrime powered by AWS</span></h2>
                <p className="text-gray mt-2">(Price is for AWS hosting. TallyPrime license required separately)</p>
              </div>
            </div>
          </div>
          <div className="row pt-4 px-3">
            <div className="col-lg-8 offset-lg-2">
              <div>
                <div className="row gap-4 gap-md-0">
                  {/* Silver Plan */}
                  <div className="col-md-6 cardContainer" id="TP_AWS_1">
                    <div className="card p-3 py-4 h-100 custom-shadow-bottom border">
                      <h4 className="text-theme">TallyPrime Pack</h4>
                      <div className="aws-desc text-gray">
                        <p className="mb-4">TallyPrime latest release pre-installed</p>
                      </div>
                      <div className="col-12 p-0">
                        <p className="mb-2">User Pack</p>
                        <select 
                          name="tponawsPersonal" 
                          className="w-100 p-2 border mb-3 rounded awsPackageSelect"
                          value={personalPack}
                          onChange={handlePersonalPackChange}
                        >
                          <option value="1">Personal (For 1 user)</option>
                          <option value="2">Regular (For 2 user)</option>
                          <option value="4">Regular Plus (For 4 user)</option>
                          <option value="8">Regular Pro (For 8 user)</option>
                          <option value="12">Performance (For 12 user)</option>
                          <option value="16">Performance Plus (For 16 user)</option>
                        </select>
                      </div>
                      <p><small className="clsPackType">{personalPackNames[personalPack]}</small></p>
                      <span className="pricing-card-badge">Most Popular</span>
                      <h3 className="price"><span>₹ </span>{personalPackDetails[personalPack].price}</h3>
                      <p className="text-gray mb-3 gst">+18% GST (<span>₹ </span>{personalPackDetails[personalPack].gst})</p>
                      <a className="arrow-animation">
                        <button className="btn btn-primary w-100 new">Buy Now
                          <img className="arrow" src="https://resources.tallysolutions.com/wp-content/themes/tally/assets/images/arrow-up-right-sharp.svg" alt="arrow-up-right-blue" />
                        </button>
                      </a>
                      <h5 className="my-4">What you get:</h5>
                      <ul className="feature-list mb-0">
                        {personalPackDetails[personalPack].specs.map((spec, index) => (
                          spec.includes("24*7, Unlimited Usage") ? (
                            <li key={index} className="unlimited-usage">
                              <img src="https://resources.tallysolutions.com/wp-content/themes/tally/assets/images/customer/blue-tick.svg" alt="Check" /> 
                              {spec}
                            </li>
                          ) : (
                            <li key={index}>
                              <img src="https://resources.tallysolutions.com/wp-content/themes/tally/assets/images/customer/black-tick.svg" alt="Check" /> 
                              {spec}
                            </li>
                          )
                        ))}
                      </ul>
                      <p className="text-gray mt-3">
                        <small>Available in 3, 6 & 12 months subscription period</small>
                      </p>
                    </div>
                  </div>

                  {/* Gold Plan */}
                  <div className="col-md-6 cardContainer" id="TP_SERVER_AWS_1">
                    <div className="card p-3 py-4 position-relative h-100 custom-shadow-bottom border">
                      <h4 className="text-theme">TallyPrime Server Pack</h4>
                      <div className="aws-desc text-gray">
                        <p>TallyPrime and TallyPrime server latest release pre-installed</p>
                      </div>
                      <div className="col-12 p-0">
                        <p className="mb-2">User Pack</p>
                        <select 
                          name="tponawsServer" 
                          className="w-100 p-2 border mb-3 rounded awsPackageSelect"
                          value={serverPack}
                          onChange={handleServerPackChange}
                        >
                          <option value="8">Performance (For 8 user)</option>
                          <option value="12">Performance Max (For 12 user)</option>
                          <option value="16">Performance Plus (For 16 user)</option>
                          <option value="24">Performance Max Plus (For 24 user)</option>
                          <option value="32">Performance Pro (For 32 user)</option>
                          <option value="48">Enterprise (For 48 user)</option>
                          <option value="64">Enterprise Plus (For 64 user)</option>
                          <option value="96">Enterprise Max Plus (For 96 user)</option>
                          <option value="128">Enterprise Pro (For 128 user)</option>
                        </select>
                      </div>
                      <p><small className="clsPackType">{serverPackNames[serverPack]}</small></p>
                      <h3 className="price"><span>₹ </span>{serverPackDetails[serverPack].price}</h3>
                      <p className="text-gray mb-3">+18% GST (₹ {serverPackDetails[serverPack].gst})</p>
                      <a className="arrow-animation">
                        <button className="btn btn-primary w-100 new">Buy Now
                          <img className="arrow" src="https://resources.tallysolutions.com/wp-content/themes/tally/assets/images/arrow-up-right-sharp.svg" alt="arrow-up-right-blue" />
                        </button>
                      </a>
                      <h5 className="my-4">What you get:</h5>
                      <ul className="feature-list mb-0">
                        {serverPackDetails[serverPack].specs.map((spec, index) => (
                          spec.includes("24*7, Unlimited Usage") ? (
                            <li key={index} className="unlimited-usage">
                              <img src="https://resources.tallysolutions.com/wp-content/themes/tally/assets/images/customer/blue-tick.svg" alt="Check" /> 
                              {spec}
                            </li>
                          ) : (
                            <li key={index}>
                              <img src="https://resources.tallysolutions.com/wp-content/themes/tally/assets/images/customer/black-tick.svg" alt="Check" /> 
                              {spec}
                            </li>
                          )
                        ))}
                      </ul>
                      <p className="text-gray mt-3">
                        <small>Available in 3, 6 & 12 months subscription period</small>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq py-5 section">
        <div className="container py-5">
          <div className="text-center mb-5">
            <SectionTitle 
              title="Frequently Asked Questions" 
              description1="Common" 
              description2="Questions" 
            />
          </div>
          
          <div className="row mt-5 position-relative">
            <div className="col-lg-8 mx-auto">
              <div className="position-absolute" style={{ background: "rgba(13, 110, 253, 0.05)", width: "200px", height: "200px", borderRadius: "50%", top: "-100px", right: "-150px", zIndex: 0 }}></div>
              
              <div className="accordion" id="awsFaqAccordion">
                {faqs.map((faq, index) => (
                  <div className="accordion-item mb-4 border rounded-3 overflow-hidden position-relative" key={index} style={{ zIndex: 1 }}>
                    <h2 className="accordion-header" id={`heading${index}`}>
                      <button 
                        className={`accordion-button ${index !== 0 ? 'collapsed' : ''}`} 
                        type="button" 
                        data-bs-toggle="collapse" 
                        data-bs-target={`#collapse${index}`} 
                        aria-expanded={index === 0 ? 'true' : 'false'} 
                        aria-controls={`collapse${index}`}
                        style={{ padding: '20px', background: index === 0 ? 'linear-gradient(to right, #f8f9fa, #ffffff)' : '#ffffff' }}
                      >
                        <div className="d-flex align-items-center">
                          <span className="me-3 rounded-circle d-flex align-items-center justify-content-center" style={{ width: '30px', height: '30px', minWidth: '30px', backgroundColor: 'rgba(13, 110, 253, 0.1)', color: '#0d6efd' }}>Q</span>
                          <span>{faq.question}</span>
                        </div>
                      </button>
                    </h2>
                    <div 
                      id={`collapse${index}`} 
                      className={`accordion-collapse collapse ${index === 0 ? 'show' : ''}`} 
                      aria-labelledby={`heading${index}`} 
                      data-bs-parent="#awsFaqAccordion"
                    >
                      <div className="accordion-body" style={{ padding: '20px 20px 20px 65px' }}>
                        <div className="d-flex">
                          <span className="me-3 rounded-circle d-flex align-items-center justify-content-center position-absolute" style={{ width: '30px', height: '30px', backgroundColor: 'rgba(13, 110, 253, 0.1)', color: '#0d6efd', left: '20px' }}>A</span>
                          <p className="mb-0">{faq.answer}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="position-absolute" style={{ background: "rgba(13, 110, 253, 0.05)", width: "150px", height: "150px", borderRadius: "50%", bottom: "-70px", left: "-100px", zIndex: 0 }}></div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta py-5 section bg-primary text-white">
        <div className="container py-5 text-center">
          <h2 className="text-white mb-4">Ready to take your business to the cloud?</h2>
          <p className="lead mb-4">Get TallyPrime powered by AWS today and enjoy the flexibility of cloud computing.</p>
          <a href="#contact" className="btn btn-light btn-lg">Contact Us</a>
        </div>
      </section>
    </div>
  );
};

export default TallyPrimeAWS; 